import { twJoin } from 'tailwind-merge';

import type { EvaluatedResult } from 'components/Calculator/config/types';
import type { FC } from 'react';

interface CalculatorResultRowProps {
  /** Config errors are for whoever is building the calculator, not for readers. */
  showConfigErrors: boolean;
  result: EvaluatedResult;
  /** True when a field the reader has to fill in is currently empty. */
  waitingForInput: boolean;
}

/**
 * One result row. A derived result, meaning one computed from other results
 * rather than straight from the reader's figures, is drawn outlined rather than
 * filled so it reads as a conclusion drawn from the rows above it.
 */
const CalculatorResultRow: FC<CalculatorResultRowProps> = ({ result, showConfigErrors, waitingForInput }) => {
  const { derived, display, error, label } = result;
  const hasFigure = error === undefined;

  return (
    <div
      className={twJoin(
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg px-4 py-3.5 sm:px-5 sm:py-4',
        derived ? 'border border-dashed border-primary-400' : 'bg-primary-100',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-md font-medium text-primary-900 sm:text-lg">{label}</span>
        {derived && <span className="text-xs font-semibold uppercase tracking-wide text-primary-700">Derived</span>}
      </div>

      {hasFigure && (
        <span className="text-2xl font-bold text-primary-1100 sm:text-3xl" data-calculator-result={result.key}>
          {display}
        </span>
      )}

      {!hasFigure && waitingForInput && <span className="text-md text-primary-700">Enter a value</span>}

      {/* An error with every field filled in means the formula itself is wrong,
          which only the editor can fix. Readers get nothing rather than a
          message they can do nothing about. */}
      {!hasFigure && !waitingForInput && showConfigErrors && <span className="text-md text-error-700">{error}</span>}
    </div>
  );
};

export default CalculatorResultRow;
